import React, { useContext, useEffect, useRef, useState, type FC } from 'react';
import styles from './searchUserDialog.module.scss';
import { t } from 'i18next';
import { Button, Dialog, Stack } from '@mui/material';
import searchIcon from '../../../../assets/search.svg';
import { AdminContext, EmailandName } from '../../../../context/AdminProvider';
import ObrasAPI from '../../../../services/APIObras';
import { AdminAssignmentCandidate, CenterParams, UsuarioProyecto } from '../../../../types/Admin';
import spinner from '../../../../assets/loader_export.svg';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SearchUserDialogProps {
  open: boolean;
  handleOpen: (b: boolean) => void;
  users: UsuarioProyecto[];
  selectedProject: CenterParams;
}

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const SearchUserDialog: FC<SearchUserDialogProps> = (props) => {
  const { open, handleOpen, users, selectedProject } = props;
  const [value, setValue] = useState('');
  const api = ObrasAPI.getInstance();
  const adminContext = useContext(AdminContext);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSequence = useRef(0);
  const loadingRef = useRef(false);
  const [filteredUsers, setFilteredUsers] = useState<AdminAssignmentCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [failedPage, setFailedPage] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const invalidate = () => {
    requestSequence.current += 1;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = null;
    loadingRef.current = false;
  };

  useEffect(() => {
    invalidate();
    setValue('');
    setFilteredUsers([]);
    setLoading(false);
    setNoResults(false);
    setFailedPage(null);
    setPage(0);
    setHasNextPage(false);
    return invalidate;
  }, [open, selectedProject?.codCentro, selectedProject?.codSupercentro, selectedProject?.codZona]);

  const loadCandidates = async (search: string, nextPage: number, sequence: number) => {
    loadingRef.current = true;
    setLoading(true);
    setFailedPage(null);
    setNoResults(false);
    try {
      const result = await api.getAssignmentCandidates(search, nextPage);
      if (sequence !== requestSequence.current) return;
      setFilteredUsers(previous => {
        const usersById = new Map((nextPage === 0 ? [] : previous).map(user => [user.id, user]));
        result.data.forEach(user => usersById.set(user.id, user));
        return Array.from(usersById.values());
      });
      setPage(result.page);
      setHasNextPage(result.hasNextPage);
      setNoResults(nextPage === 0 && result.data.length === 0);
    } catch {
      if (sequence === requestSequence.current) setFailedPage(nextPage);
    } finally {
      if (sequence === requestSequence.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  };

  const handleChangeInput = (newValue: string) => {
    invalidate();
    setValue(newValue);
    setFilteredUsers([]);
    setFailedPage(null);
    setNoResults(false);
    setPage(0);
    setHasNextPage(false);
    const search = newValue.trim();
    const sequence = requestSequence.current;
    setLoading(search.length >= 3);
    if (search.length < 3) return;
    loadingRef.current = true;
    searchTimeout.current = setTimeout(() => {
      searchTimeout.current = null;
      void loadCandidates(search, 0, sequence);
    }, 350);
  };

  const closeDialog = () => {
    invalidate();
    handleOpen(false);
  };

  const handleEditUser = (name: string, email: string) => {
    const userToEdit: EmailandName = { name, email};
    adminContext.handleChangeUserToEdit(userToEdit);
    const projectToEdit = {
      codEmpresa: '0008',
      codZona: selectedProject.codZona,
      codCentro: selectedProject.codCentro,
      codSupercentro: selectedProject.codSupercentro
    };
    adminContext.handleChangePermissionToEdit(undefined);
    adminContext.handleChangeProjectToAdd(projectToEdit);
    adminContext.handleChangeHasProject(true);
    adminContext.handleSetIsEdition(true);
  };
  return (
    <div data-testid='searchUserDialog-test' className={styles.searchUserDialog}>
      <Dialog
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.15)' } }}
        PaperProps={{ sx: { borderRadius: '20px' } }}
        open={open}
        onClose={closeDialog}
      >
        <div className={styles.dialogContent}>
          <p className={styles.header}>{t('administration.searchDialog.searchUser')}</p>
          <p className={styles.text}>{t('administration.searchDialog.assignmentSearchTip')}</p>
          <div className={styles.userInput}>
            <div className={styles.userInputTitle}>
              {t('administration.user')}
            </div>
            <input
              className={styles.inputField}
              value={value}
              aria-label={String(t('administration.user'))}
              onChange={(e) => handleChangeInput(e.target.value)}
            />
            <img className={styles.searchIcon} src={searchIcon} />
          </div>
          <div className={styles.filteredUsers}>
            {loading && (
              <div className={styles.loaderContainer}>
                <img src={spinner} alt="loading..." width="30px" className={styles.loader} />
              </div>
            )}

            {(
              <>
                <table>
                  <tbody>
                    {filteredUsers.map((filtered) => {
                      const isIncluded = users.some(u => u.email.trim().toLowerCase() === filtered.email.trim().toLowerCase());
                      return (
                        <tr key={filtered.id}>
                          <td>
                            <div className={styles.userIdentity}>
                              <span className={styles.userName}>{filtered.nombre}</span>
                              <span className={styles.userEmail}>{filtered.email}</span>
                            </div>
                          </td>
                          <td>
                            <div className={styles.addProjectContainer}>
                              <button
                                type="button"
                                disabled={isIncluded || loading}
                                className={isIncluded ? styles.isIncluded : styles.notIncluded}
                                onClick={() => {
                                  if(!isIncluded){
                                    handleEditUser(filtered.nombre, filtered.email);
                                    closeDialog();
                                  }}
                                }
                              >
                                + {
                                  isIncluded
                                    ? t('administration.searchDialog.addedToProject')
                                    : t('administration.searchDialog.addToProject')
                                }
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {noResults && <p>{t('administration.table.notFound')}</p>}
                {failedPage !== null && <div role="alert">
                  <p>{t('administration.searchDialog.searchError')}</p>
                  <Button onClick={() => void loadCandidates(value.trim(), failedPage, requestSequence.current)}>
                    {t('administration.searchDialog.retry')}
                  </Button>
                </div>}
                {hasNextPage && failedPage === null && <Button disabled={loading} onClick={() => {
                  if (!loadingRef.current) void loadCandidates(value.trim(), page + 1, requestSequence.current);
                }}>
                  {t('administration.searchDialog.loadMore')}
                </Button>}
              </>
            )}
          </div>
          <Stack spacing={2} direction="row" justifyContent="center">
            <div className={styles.buttonClose} onClick={closeDialog}>{t('administration.searchDialog.close')}</div>
          </Stack>
        </div>
      </Dialog>
    </div>
  );
};

export default SearchUserDialog;
